import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Paper,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MicIcon from '@mui/icons-material/Mic';
import Header from './components/Header';
import ReviewComponent from './components/ReviewComponent';
import { logUserEvent } from './utils/logger';

function PodcastPlayer() {
  const { courseId } = useParams();
  const audioRef = useRef(null);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch the media URL from the API
    const fetchMediaUrl = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/student/courses/${courseId}/media/podcast`);
        if (!response.ok) {
          throw new Error('Failed to load podcast');
        }
        const data = await response.json();
        setMediaUrl(data.url);
        setError(null);
        logUserEvent('material_access', 'Accessed podcast', { course_id: courseId, material_type: 'podcast' });
      } catch (err) {
        console.error('Error loading podcast:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMediaUrl();
  }, [courseId]);

  useEffect(() => {
    // Add additional protection against downloading
    const audio = audioRef.current;
    if (audio) {
      // Disable right-click context menu
      audio.addEventListener('contextmenu', (e) => e.preventDefault());

      // Disable drag and drop
      audio.addEventListener('dragstart', (e) => e.preventDefault());

      // Disable text selection
      audio.style.userSelect = 'none';
      audio.style.webkitUserSelect = 'none';

      // Add additional attributes to make downloading harder
      audio.setAttribute('controlsList', 'nodownload noremoteplayback');
    }
  }, []);

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0054A6 0%, #00386e 55%, #001f3f 100%)'
    }}>
      <Header />
      <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton component={RouterLink} to="/dashboard" aria-label="Back" sx={{ color: 'white' }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h5" component="h1" sx={{
            flexGrow: 1,
            color: 'white',
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
          }}>
            Course Podcast
          </Typography>
        </Stack>

        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{ mb: 3 }}>
            <MicIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Course Audio Content
            </Typography>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 100 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Typography color="error" sx={{ my: 2 }}>
              Error loading podcast: {error}
            </Typography>
          )}

          {!loading && !error && mediaUrl && (
            <audio
              ref={audioRef}
              width="100%"
              controls
              controlsList="nodownload noremoteplayback"
              style={{
                width: '100%',
                maxWidth: '600px',
                userSelect: 'none',
                WebkitUserSelect: 'none'
              }}
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
            >
              <source
                src={mediaUrl}
                type="audio/mpeg"
              />
              Your browser does not support the audio tag.
            </audio>
          )}
        </Paper>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          This audio content is protected. Downloading or sharing is not permitted.
        </Typography>

        {/* Review Component - only shown for @deepalert.ai users */}
        <ReviewComponent courseId={courseId} materialType="podcast" />
      </Container>
    </Box>
  );
}

export default PodcastPlayer;
